import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { Splash } from "@/components/shell/splash";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { UpgradeDialog } from "@/components/billing/upgrade-dialog";
import { PlanShell } from "@/components/plans/plan-shell";
import { PickPlans, ReadSheets, type ImportSheet } from "@/components/plans/add-step";
import { ScaleStep } from "@/components/plans/scale-step";
import { ReviewStep } from "@/components/plans/review-step";
import { HouseStep } from "@/components/plans/house-step";
import { said } from "@/lib/errors";
import { cadKind, sheetPictures, type CadPick, type SheetPicture } from "@/components/plans/sheet-pictures";
import {
  buildDraft,
  defaultTargets,
  editsWithoutSheet,
  IMPORT_STEPS,
  NEW_ROOM,
  fromSaved,
  NO_EDITS,
  toSaved,
  type DraftEdits,
  type ImportStep,
  type RoomEdit,
} from "@/lib/house-draft";
import { useUploadFile } from "@/lib/use-upload-file";

export const Route = createFileRoute("/plans")({
  // ?id=<import> picks up plans already being read.
  validateSearch: (search): { id?: string } => ({
    id: typeof search.id === "string" ? search.id : undefined,
  }),
  component: PlansPage,
});

function PlansPage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <PlanImport />
      </Authenticated>
      <Toaster />
    </>
  );
}

/**
 * Bringing in a household's plans, in four steps: add the sheets (read by
 * the model as soon as they are in), check each floor plan's scale, review
 * the rooms over the plan, then make them: rooms of one house, each to its
 * real size, filling the room boards the household has or new ones.
 */
function PlanImport() {
  const { t } = useTranslation("plans");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const { id } = useSearch({ from: "/plans" });
  const current = useQuery(api.planImports.current);
  const importId = (id ?? current ?? null) as Id<"planImports"> | null;
  const doc = useQuery(api.planImports.get, importId ? { importId } : "skip");
  const available = useQuery(api.planImports.available);
  const boards = useQuery(api.boards.listForMyWorkspace);
  const plan = useQuery(api.billing.getMyPlan);
  const workspace = useQuery(api.workspaces.getMyWorkspace);
  const houses = useQuery(api.houses.listMine);
  const start = useMutation(api.planImports.start);
  const retry = useMutation(api.planImports.retry);
  const removeSheet = useMutation(api.planImports.removeSheet);
  const discard = useMutation(api.planImports.discard);
  const createRooms = useMutation(api.planImports.createRooms);
  const saveDraft = useMutation(api.planImports.saveDraft);
  const upload = useUploadFile();

  const [picked, setPicked] = useState<(SheetPicture | CadPick)[]>([]);
  // A CAD file a free household dropped: named in the note that says it's a Plus feature.
  const [cadRefused, setCadRefused] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [busy, setBusy] = useState<false | "uploading" | "creating">(false);
  const [stepState, setStep] = useState<ImportStep | null>(null);
  const [edits, setEdits] = useState<DraftEdits>(NO_EDITS);
  const [houseNameState, setHouseName] = useState<string | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // The import whose saved draft the screens have taken up: until then nothing is saved over it.
  const [restored, setRestored] = useState<string | null>(null);
  const [confirmingStartOver, setConfirmingStartOver] = useState(false);
  if (doc && restored !== doc._id) {
    setRestored(doc._id);
    const saved = doc.draft ? fromSaved(doc.draft) : null;
    setEdits(saved?.edits ?? NO_EDITS);
    setHouseName(saved?.houseName ?? null);
    // Plans nothing was done with yet start where they were added: their sheets are read there.
    setStep(saved?.step ?? "add");
  }

  const sheets: ImportSheet[] = useMemo(() => doc?.sheets ?? [], [doc]);
  const draft = useMemo(() => buildDraft(sheets, edits), [sheets, edits]);
  const included = draft.floors.flatMap((f) => f.rooms.filter((r) => r.include));
  const allBoards = useMemo(() => boards?.boards ?? [], [boards]);
  // Plans brought in again go to the latest house unless renamed: the server fills a house by its name.
  const houseName = houseNameState ?? houses?.at(-1)?.name ?? workspace?.name ?? "";
  const existingHouse =
    houses?.find((h) => h.name.trim().toLowerCase() === houseName.trim().toLowerCase())?._id ?? null;
  const targets = useMemo(() => {
    const chosen = Object.fromEntries(
      Object.entries(edits.rooms).flatMap(([key, edit]) => (edit.target ? [[key, edit.target]] : [])),
    );
    const open = included.filter((r) => !chosen[r.key]);
    const taken = new Set(Object.values(chosen));
    return { ...defaultTargets(open, allBoards.filter((b) => !taken.has(b._id)), existingHouse), ...chosen };
  }, [included, edits.rooms, allBoards, existingHouse]);

  const reading = doc?.status === "reading";
  const scaled = draft.floors.length > 0 && draft.floors.every((f) => f.scale);
  const problems = included.filter((r) => r.problem).length;
  const reached = !doc || reading ? 0 : !scaled ? 1 : 3;
  const step: ImportStep = stepState ?? (doc && !reading ? "scale" : "add");
  const at = IMPORT_STEPS.indexOf(step);
  const go = (next: ImportStep) => {
    setStep(next);
    window.scrollTo({ top: 0 });
  };

  // Everything done on the screens is kept as it happens, a moment after the last change.
  const live = Boolean(doc && restored === doc._id && doc.status !== "done");
  useEffect(() => {
    if (!live || !importId) return;
    const timer = setTimeout(() => {
      saveDraft({ importId, draft: toSaved(step, houseNameState, edits) }).catch(() => {
        toast.error(t("import.draftFailed"));
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [live, importId, step, houseNameState, edits, saveDraft, t]);

  const edit = (key: string, change: RoomEdit | null) =>
    setEdits((all) => ({ ...all, rooms: { ...all.rooms, [key]: change ? { ...all.rooms[key], ...change } : {} } }));

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setPreparing(true);
    try {
      for (const file of files) {
        const cad = cadKind(file);
        if (cad) {
          if (plan?.plan !== "plus") setCadRefused(file.name);
          else setPicked((all) => [...all, { cad, file, fileName: file.name }].slice(0, 12));
          continue;
        }
        try {
          const pages = await sheetPictures(file);
          setPicked((all) => [...all, ...pages].slice(0, 12));
        } catch {
          toast.error(t("import.add.unreadable", { name: file.name }));
        }
      }
    } finally {
      setPreparing(false);
    }
  };

  const read = async () => {
    setBusy("uploading");
    try {
      const keys = await Promise.all(
        picked.map((sheet) =>
          "cad" in sheet
            ? // As it is: the sandbox reads the drawing itself.
              upload(new File([sheet.file], sheet.fileName, { type: "application/octet-stream" }))
            : upload(
            new File([sheet.blob], `${sheet.fileName}${sheet.page ? `-${sheet.page}` : ""}.${sheet.blob.type === "image/png" ? "png" : "jpg"}`, {
              type: sheet.blob.type,
            }),
          ),
        ),
      );
      const newId = await start({
        sheets: picked.map((sheet, i) =>
          "cad" in sheet
            ? { key: keys[i], fileName: sheet.fileName, width: 0, height: 0, cad: sheet.cad }
            : {
                key: keys[i],
                fileName: sheet.fileName,
                ...(sheet.page && { page: sheet.page }),
                width: sheet.width,
                height: sheet.height,
                ...(sheet.paper && { paper: sheet.paper }),
              },
        ),
      });
      setPicked([]);
      await navigate({ to: "/plans", search: { id: newId }, replace: true });
    } catch (err) {
      toast.error(said(err, t("import.add.uploadFailed")));
    } finally {
      setBusy(false);
    }
  };

  const startOver = async () => {
    setConfirmingStartOver(false);
    if (importId && doc?.status !== "done") await discard({ importId });
    setEdits(NO_EDITS);
    setStep(null);
    setHouseName(null);
    setRestored(null);
    await navigate({ to: "/plans", search: {}, replace: true });
  };

  const create = async () => {
    if (!importId) return;
    setBusy("creating");
    try {
      await createRooms({
        importId,
        houseName,
        rooms: included.map((room) => {
          const target = targets[room.key] ?? NEW_ROOM;
          return {
            target:
              target === NEW_ROOM
                ? { name: room.name, ...(room.type && { roomType: room.type }) }
                : { boardId: target as Id<"boards"> },
            room: { plan: room.plan, openings: room.openings, height: room.height, north: room.north },
            ...(room.level && { level: room.level }),
            ...(room.fixtures.length > 0 && { fixtures: room.fixtures }),
            place: room.origin,
          };
        }),
      });
      toast.success(t("import.house.created", { count: included.length }));
      await navigate({ to: "/" });
    } catch (err) {
      toast.error(said(err, t("import.house.failed")));
    } finally {
      setBusy(false);
    }
  };

  if (current === undefined || (importId && doc === undefined) || boards === undefined) {
    return <Splash subtitle={tc("status.loading")} />;
  }
  const titles: Record<ImportStep, React.ReactNode> = {
    add: t("import.titles.add"),
    scale: t("import.titles.scale"),
    review: t("import.titles.review", { count: included.length }),
    house: t("import.titles.house"),
  };
  const back =
    at > 0 ? (
      <Button variant="outline" onClick={() => go(IMPORT_STEPS[at - 1])} disabled={busy !== false}>
        {tc("actions.back")}
      </Button>
    ) : null;

  const fresh = !doc || doc.status === "done";
  const footer =
    step === "add" ? (
      <>
        <p className="text-xs text-ink-3">{fresh ? t("import.add.private") : t("import.saved")}</p>
        {fresh ? (
          <Button size="lg" onClick={read} disabled={picked.length === 0 || preparing || busy !== false || available === false}>
            {busy === "uploading" ? t("import.add.uploading") : t("import.add.read")}
          </Button>
        ) : (
          <Button size="lg" onClick={() => go("scale")} disabled={reading}>
            {reading ? t("import.add.stillReading") : tc("actions.continue")}
          </Button>
        )}
      </>
    ) : step === "scale" ? (
      <>
        {back}
        <Button size="lg" onClick={() => go("review")} disabled={!scaled}>
          {tc("actions.continue")}
        </Button>
      </>
    ) : step === "review" ? (
      <>
        {back}
        <div className="flex items-center gap-3">
          {problems > 0 ? <p className="text-xs text-destructive">{t("import.review.fixFirst", { count: problems })}</p> : null}
          <Button size="lg" onClick={() => go("house")} disabled={included.length === 0 || problems > 0}>
            {t("import.review.looksRight")}
          </Button>
        </div>
      </>
    ) : (
      <>
        {back}
        <Button size="lg" onClick={create} disabled={busy !== false || included.length === 0 || !houseName.trim()}>
          {busy === "creating"
            ? tc("actions.saving")
            : t("import.house.create", { count: included.length })}
        </Button>
      </>
    );

  return (
    <>
      <PlanShell
        step={step}
        reached={reached}
        onStep={go}
        title={titles[step]}
        helper={<Trans t={t} i18nKey={`import.helpers.${step}`} />}
        footer={footer}
        onStartOver={fresh ? undefined : () => setConfirmingStartOver(true)}
      >
        {step === "add" ? (
          <>
            {available === false ? (
              <p className="rounded-lg border border-line bg-paper-2/60 px-4 py-3 text-sm text-ink-2">{t("import.unavailable")}</p>
            ) : null}
            {fresh && cadRefused ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent/40 bg-accent-soft/50 px-4 py-3">
                <p className="max-w-2xl text-sm text-ink">
                  <span className="font-medium">{t("import.cad.plusTitle", { name: cadRefused })}</span>{" "}
                  {t("import.cad.plusBody")}
                </p>
                <Button size="sm" onClick={() => setUpgradeOpen(true)}>
                  {t("import.cad.upgrade")}
                </Button>
              </div>
            ) : null}
            {fresh ? (
              <PickPlans
                picked={picked}
                preparing={preparing}
                onAdd={addFiles}
                onRemove={(i) => setPicked((all) => all.filter((_, j) => j !== i))}
              />
            ) : (
              <ReadSheets
                sheets={sheets}
                onRetry={(index) =>
                  importId &&
                  void retry({ importId, index }).catch((err) =>
                    toast.error(said(err, t("import.add.failed"))),
                  )
                }
                onRemove={(index) => {
                  if (!importId) return;
                  // The server renumbers the saved draft; these edits, saved over it next, follow suit.
                  setEdits((all) => editsWithoutSheet(all, index));
                  void removeSheet({ importId, index });
                }}
              />
            )}
          </>
        ) : step === "scale" ? (
          <ScaleStep
            sheets={sheets}
            draft={draft}
            onScale={(sheet, mpp) =>
              setEdits((all) => {
                const scales = { ...all.scales };
                if (mpp) scales[sheet] = mpp;
                else delete scales[sheet];
                return { ...all, scales };
              })
            }
          />
        ) : step === "review" ? (
          <ReviewStep sheets={sheets} draft={draft} onEdit={edit} />
        ) : (
          <HouseStep
            rooms={included}
            targets={targets}
            boards={allBoards}
            houseName={houseName}
            existingHouse={existingHouse !== null}
            onHouseName={setHouseName}
            onTarget={(key, target) => edit(key, { target })}
          />
        )}
      </PlanShell>
      <Dialog open={confirmingStartOver} onOpenChange={setConfirmingStartOver}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("import.startOverTitle")}</DialogTitle>
            <DialogDescription>{t("import.startOverBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmingStartOver(false)}>
              {tc("actions.cancel")}
            </Button>
            <Button variant="destructive" onClick={startOver}>
              {t("import.startOver")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={t("import.cad.upgradeReason")} />
    </>
  );
}
